/**
 * AddCard: card dashed con "+" para agregar una nueva entidad a una lista.
 *
 * Sprint 14 S14-06c. Replica `.add-card` del mock `docs/dev/ideas/mis-datos.html`
 * (líneas 557-580). NO está en el handoff canónico todavía
 * (`design-system/components.css`) porque es un patrón específico del
 * grid de vehículos/residentes de la página Mis Datos rediseñada (Bloque
 * C del Sprint 14).
 *
 * DISTINCIÓN con `EmptyState`:
 * - `EmptyState` → reemplaza la lista cuando no hay items.
 * - `AddCard` → se suma al grid cuando ya hay items (queda como última
 *   card del grid, marcada visualmente como "agregar nuevo").
 *
 * Visual: card dashed con círculo "+" centrado + label. En hover se tinta
 * naranja (color de marca + border naranja + fondo naranja-soft).
 *
 * Render como `<button>` para accesibilidad keyboard (Enter/Space activan).
 *
 * Uso:
 *   <div className="grid grid-cols-2 gap-3">
 *     {vehiculos.map((v) => <VehicleCard key={v.id} v={v} />)}
 *     <AddCard label="Agregar otro vehículo" onClick={() => setOpen(true)} />
 *   </div>
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react';

interface AddCardProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Texto al pie del círculo "+". Ej. "Agregar otro vehículo". */
  label: ReactNode;
}

export function AddCard({
  label,
  className = '',
  type = 'button',
  ...rest
}: AddCardProps) {
  // Replica del .add-card: border dashed line-strong, rounded-md, padding 22px,
  // min-height 160px, hover: text-orange + border-orange/40 + bg-orange/20.
  return (
    <button
      type={type}
      className={`group bg-transparent border border-dashed border-line-strong rounded-md p-[22px] min-h-[160px] flex flex-col items-center justify-center gap-2 cursor-pointer text-text-secondary text-md font-sans transition-colors duration-fast hover:text-orange hover:border-orange/40 hover:bg-orange/20 ${className}`.trim()}
      {...rest}
    >
      <span className="w-9 h-9 rounded-full bg-white/[0.04] border-[0.5px] border-line-strong flex items-center justify-center transition-colors duration-fast group-hover:bg-orange/20 group-hover:border-orange/40">
        <PlusIcon />
      </span>
      <span>{label}</span>
    </button>
  );
}

function PlusIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      width="18"
      height="18"
      aria-hidden="true"
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}
